import { z } from "zod";
import { baseSpecShape, registerShell } from "./base.js";
import { looksLikeEmoji } from "../text.js";
export const pickSpecSchema = z.object({
    type: z.literal("pick"),
    ...baseSpecShape,
    options: z
        .array(z.object({
        label: z.string().min(1).max(40),
        /*
          Dropped rather than rejected when it isn't a picture. The model has
          used this slot for a list marker — a circled `1` — which renders at
          26px next to the label and reads as damage. Losing the badge costs
          nothing; failing the check over it would cost the child their star.
        */
        emoji: z
            .string()
            .max(4)
            .optional()
            .transform((value) => (value && looksLikeEmoji(value) ? value : undefined)),
        correct: z.boolean().optional(),
    }))
        .min(2)
        .max(4),
});
export const pickShell = registerShell({
    id: "pick",
    version: 1,
    summary: "Tap one of 2–4 options.",
    chooseWhen: "The idea comes down to one fact, one common misconception, or a true/false. The default shell — use it when nothing else clearly fits.",
    interaction: "taps the right answer out of a few choices",
    invitation: "One question, a few answers — tap the one you think is right.",
    schema: pickSpecSchema,
    required: ["options"],
    properties: {
        options: {
            type: "array",
            minItems: 2,
            maxItems: 4,
            description: "Exactly one option must have correct: true. Order does not matter — the app " +
                "shuffles the options for display, the way it does for order and match.",
            items: {
                type: "object",
                required: ["label"],
                properties: {
                    label: { type: "string", maxLength: 40 },
                    emoji: {
                        type: "string",
                        maxLength: 4,
                        description: "One actual emoji character, or omit the field. Never a number, a letter, " +
                            "a bullet or an escape sequence — the app lays the options out itself.",
                    },
                    correct: { type: "boolean" },
                },
            },
        },
    },
    semanticIssues(spec) {
        const issues = [];
        const correct = spec.options.filter((o) => o.correct === true);
        if (correct.length !== 1) {
            issues.push(`pick needs exactly one option with correct: true, found ${correct.length}.`);
        }
        const labels = new Set(spec.options.map((o) => o.label.trim().toLowerCase()));
        if (labels.size !== spec.options.length) {
            issues.push("pick options must all be different.");
        }
        return issues;
    },
    example: {
        type: "pick",
        prompt: "Which color of light bounces around the air the MOST?",
        options: [
            { label: "Red", emoji: "🔴" },
            { label: "Blue", emoji: "🔵", correct: true },
            { label: "Yellow", emoji: "🟡" },
        ],
        right: "Yes! Blue light is small and wiggly, so the air knocks it everywhere.",
        wrong: "Close! Red light mostly zooms straight through. Blue is the bouncy one.",
    },
});
//# sourceMappingURL=pick.js.map